import React, { createContext, useContext, useEffect, useState } from 'react';
import { getSubdomain, isOnTenantSubdomain } from '../utils/subdomain';
import { tenantService } from '../services/tenantService';

const TenantContext = createContext({});

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subdomain, setSubdomain] = useState(null);

  useEffect(() => {
    loadTenantFromSubdomain();
  }, []);

  const loadTenantFromSubdomain = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Extract subdomain from URL
      const currentSubdomain = getSubdomain();
      setSubdomain(currentSubdomain);
      
      // If no subdomain, we're on main domain
      if (!currentSubdomain) {
        setTenant(null);
        setLoading(false);
        return;
      }
      
      // Fetch tenant data by subdomain
      const tenantData = await tenantService?.getTenantBySubdomain(currentSubdomain);
      
      if (!tenantData) {
        setError(`Tenant not found for subdomain: ${currentSubdomain}`);
        setTenant(null);
      } else {
        setTenant(tenantData);
      }
    } catch (err) {
      console.error('Error loading tenant:', err);
      setError(err?.message || 'Failed to load tenant');
      setTenant(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshTenant = async () => {
    await loadTenantFromSubdomain();
  };

  const value = {
    tenant,
    subdomain,
    loading,
    error,
    isOnTenantSubdomain: isOnTenantSubdomain(),
    refreshTenant
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;